import { Component, input, output, viewChild, OnInit, OnDestroy, effect } from '@angular/core';
import { AbstractControl, UntypedFormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, mergeMap, takeWhile, tap } from 'rxjs/operators';
import { Model } from '@models';
import { AwareHttpRequest } from '@appbolaget/aware-http';
import { Observable, Subject } from 'rxjs';
import { MatFormFieldAppearance, MatFormFieldModule, SubscriptSizing } from '@angular/material/form-field';
import { MatOptionModule } from '@angular/material/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { AsyncPipe, NgClass } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { Select } from '@ngxs/store';
import { UnitState } from 'app/state/unit.state';
import { Unit } from '@appbolaget/aware-model';

@Component({
    selector: 'app-multiselect-2',
    templateUrl: 'multiselect-2.component.html',
    styleUrls: ['./multiselect-2.component.scss'],
    imports: [
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        FormsModule,
        ReactiveFormsModule,
        MatCheckboxModule,
        NgClass,
        MatButtonModule,
        MatIconModule,
        MatProgressBarModule,
        MatOptionModule,
        AsyncPipe,
    ],
})
export class AwareMultiSelectComponent implements OnInit, OnDestroy {
    _request: AwareHttpRequest;

    ALIVE: boolean;
    HAS_OPENED: boolean;
    IS_LOADING: boolean;

    // Input signals
    selectPlaceholder = input<string>('Sök...');
    placeholder = input<string>();
    options = input<Array<any>>([{ title: 'Vänta...', disabled: true }]);
    disabled = input<boolean>(false);
    display = input<string>('title');
    valueProperty = input<string>('uuid');
    control = input<AbstractControl>(new UntypedFormControl());
    errorMsg = input<string>('Field is required');
    showErrorMsg = input<boolean>(false);
    selectedOptions = input<any>();
    multiple = input<boolean>(true);
    label = input<string>();
    enableSearch = input<boolean>(true);
    labelCount = input<number>(2);
    appearance = input<MatFormFieldAppearance>('fill');
    request = input<AwareHttpRequest>();
    subscriptSizing = input<SubscriptSizing>('fixed');

    // Output signals
    selectionChange = output<any>();

    // ViewChild signal
    selectElem = viewChild<MatSelect>('selectElem');

    filteredOptions: Array<any> = [];
    selectedValue: Array<any> = [];
    selectAllChecked = false;
    displayString = '';

    refresher$: Subject<any> = new Subject();

    @Select(UnitState.activeUnit) activeUnit$: Observable<Unit>;
    constructor() {
        // Effect to handle changes to disabled state
        effect(() => {
            const isDisabled = this.disabled();
            const ctrl = this.control();
            if (isDisabled) {
                ctrl.disable();
            } else {
                ctrl.enable();
            }
        });

        // Effect to handle changes to options and selectedOptions
        effect(() => {
            this.filteredOptions = this.options();
            const selectedOpts = this.selectedOptions();
            const ctrlValue = this.control().value;

            if (selectedOpts) {
                this.selectedValue = selectedOpts;
            } else if (ctrlValue && ctrlValue.length) {
                this.selectedValue = ctrlValue;
            }
        });
    }

    ngOnInit() {
        this.ALIVE = true;

        this._initListeners();
    }

    ngOnDestroy() {
        this.ALIVE = false;
    }

    get formControl(): UntypedFormControl {
        return this.control() as UntypedFormControl;
    }

    toggleDropdown() {
        this.selectElem()?.toggle();
    }

    toggleSelectAll(val) {
        if (val.checked) {
            this.control().setValue(this.filteredOptions);
        } else {
            this.control().setValue([]);
        }

        this.selectedValue = this.control().value;
        this.selectionChange.emit(this.selectedValue);
    }

    filterItem(value) {
        this.refresher$.next(value);
    }

    hideOption(option) {
        return !(this.filteredOptions.indexOf(option) > -1);
    }

    // Returns plain strings array of filtered values
    getFilteredOptionsValues() {
        const filteredValues = [];
        this.filteredOptions.forEach((option) => {
            filteredValues.push(option[this.valueProperty()]);
        });
        return filteredValues;
    }

    get onDisplayString() {
        this.displayString = '';

        if (this.selectedValue && this.selectedValue.length) {
            let displayOption = [];
            if (this.multiple()) {
                // Multi select display
                for (let i = 0; i < this.labelCount(); i++) {
                    displayOption[i] = this.selectedValue[i];
                }

                if (displayOption.length) {
                    for (let i = 0; i < displayOption.length; i++) {
                        if (displayOption[i] && displayOption[i][this.display()]) {
                            this.displayString += displayOption[i][this.display()] + ', ';
                        }
                    }
                    this.displayString = this.displayString.slice(0, -2);
                    if (this.selectedValue.length > 1 && this.selectedValue.length > this.labelCount()) {
                        this.displayString += ` (+${this.selectedValue.length - this.labelCount()} till)`;
                    }
                }
            } else {
                // Single select display
                displayOption = this.selectedValue;

                if (displayOption.length) {
                    this.displayString = displayOption[0][this.display()];
                }
            }
        }

        return this.displayString;
    }

    onSelectionChange(val) {
        this.selectedValue = Array.isArray(val.value) ? val.value : [val.value];

        const filteredValues = this.getFilteredOptionsValues();
        let count = 0;

        if (this.multiple()) {
            this.selectedValue.filter((item) => {
                if (filteredValues.includes(item[this.valueProperty()])) {
                    count++;
                }
            });
            this.selectAllChecked = count === this.filteredOptions.length;
        }

        this.selectionChange.emit(this.selectedValue);
    }

    selectOpened(_: boolean) {
        if (!this.HAS_OPENED) {
            this.refresher$.next(null);
            this.HAS_OPENED = true;
        }
    }

    trackByFn(_, item) {
        return item.uuid;
    }

    compareFn(m1: Model, m2: Model): boolean {
        return m1 && m2 ? m1.uuid === m2.uuid : m1 === m2;
    }

    private _initListeners() {
        this.refresher$
            .pipe(
                tap(() => (this.IS_LOADING = true)),
                debounceTime(300),
                takeWhile(() => this.ALIVE),
                mergeMap((query) => this.request().parameter('query', query).execute()),
                tap((result) => {
                    this.IS_LOADING = false;

                    const filteredData = result.data.filter((obj) => !this.control().value.find((obj2) => obj2.uuid === obj.uuid));

                    this.filteredOptions = [...filteredData, ...(this.control().value || [])];
                    this.selectAllChecked = true;

                    for (const option of this.filteredOptions) {
                        if (!this.selectedValue.find((obj) => obj[this.valueProperty()] === option[this.valueProperty()])) {
                            this.selectAllChecked = false;
                        }
                    }

                    if (!this.filteredOptions.length) {
                        this.selectAllChecked = false;
                    }
                }),
            )
            .subscribe();
    }
}
